import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdmin } from "@/contexts/admin/AdminContext";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import DOMPurify from 'isomorphic-dompurify';

// Custom hook to initialize interactive components in the preview
const useArticlePreviewEnhancer = (contentRef: React.RefObject<HTMLDivElement>) => {
  useEffect(() => {
    if (!contentRef.current) return;
    
    // Function to initialize gallery carousels
    const initializeGalleryCarousels = () => {
      // Find all gallery slider containers with data-carousel attribute
      const sliderGalleries = contentRef.current?.querySelectorAll('.article-gallery-slider .carousel[data-carousel="true"]');
      
      if (!sliderGalleries || sliderGalleries.length === 0) return;
      
      // For each gallery with slider layout
      sliderGalleries.forEach(gallery => {
        const items = gallery.querySelector('.carousel-content');
        const prevBtn = gallery.parentElement?.querySelector('[data-carousel-prev="true"]');
        const nextBtn = gallery.parentElement?.querySelector('[data-carousel-next="true"]');
        
        if (!items || !prevBtn || !nextBtn) return;
        
        // Simple carousel functionality
        let scrollAmount = 0;
        const itemWidth = items.querySelector('.carousel-item')?.clientWidth || 0;
        
        // Attach click handlers to buttons
        prevBtn.addEventListener('click', () => {
          if (scrollAmount > 0) {
            scrollAmount -= itemWidth;
            items.scrollTo({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        });
        
        nextBtn.addEventListener('click', () => {
          const maxScroll = items.scrollWidth - items.clientWidth;
          if (scrollAmount < maxScroll) {
            scrollAmount += itemWidth;
            items.scrollTo({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        });
        
        // Add scrolling event to update button states
        items.addEventListener('scroll', () => {
          scrollAmount = items.scrollLeft;
          
          // Update button states
          prevBtn.classList.toggle('disabled', scrollAmount <= 0);
          nextBtn.classList.toggle('disabled', scrollAmount >= items.scrollWidth - items.clientWidth);
        });
        
        // Initialize button states
        prevBtn.classList.add('disabled');
        nextBtn.classList.toggle('disabled', items.scrollWidth <= items.clientWidth);
      });
    };
    
    // Run initializers after content is rendered
    setTimeout(() => {
      initializeGalleryCarousels();
    }, 100);
    
    // Run again on resize to handle layout changes
    window.addEventListener('resize', initializeGalleryCarousels);
    
    return () => {
      window.removeEventListener('resize', initializeGalleryCarousels);
    };
  }, [contentRef]);
};

const ArticlePreviewPage = () => {
  const { id } = useParams();
  const { articles, loadArticles } = useAdmin();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Create a ref for the content container
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Use our custom enhancer hook
  useArticlePreviewEnhancer(contentRef);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      
      if (!articles || articles.length === 0) {
        await loadArticles();
      }
      
      const foundArticle = articles?.find(a => a.id === id);
      
      if (foundArticle) {
        setArticle(foundArticle);
      }
      
      setLoading(false);
    };
    
    fetchArticle();
  }, [id, articles, loadArticles]);

  const handleBack = () => {
    navigate(-1);
  };

  // Configure DOMPurify to allow iframes for video embeds and data-attributes for galleries
  const purifyConfig = {
    ADD_TAGS: ['iframe', 'button'],
    ADD_ATTR: [
      'allowfullscreen', 
      'frameborder', 
      'allow', 
      'src', 
      'width', 
      'height',
      'title',
      'data-platform',
      'data-src',
      'data-type',
      'data-layout',
      'data-carousel',
      'data-carousel-prev',
      'data-carousel-next',
      'aria-label'
    ]
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4">Loading article preview...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto py-8">
        <div className="bg-red-50 border border-red-200 p-4 rounded-md text-red-800">
          <h1 className="text-xl font-bold mb-2">Article Not Found</h1>
          <p>The article you are looking for could not be found.</p>
          <Button variant="outline" onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const getAuthorName = (author: any) => {
    if (!author) return "Unknown Author";
    return `${author.first_name || ''} ${author.last_name || ''}`.trim();
  };

  // Sanitize HTML content while allowing iframe embeds and gallery data attributes
  const sanitizedContent = DOMPurify.sanitize(article.content, purifyConfig);

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6 flex items-center">
        <Button variant="ghost" onClick={handleBack} className="mr-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-xl font-semibold">Article Preview</h1>
      </div>
      
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        {/* Preview Header */}
        <div className="bg-muted/20 p-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                {article.status}
              </span>
            </div>
            <Button onClick={() => navigate(`/admin/articles/${article.id}`)}>
              Edit Article
            </Button>
          </div>
        </div>
        
        {/* Article Content */}
        <div className="p-6">
          {article.header_image_url && (
            <div className="mb-6">
              <img 
                src={article.header_image_url} 
                alt={article.title} 
                className="w-full max-h-96 object-cover rounded-md"
              />
            </div>
          )}
          
          <h1 className="text-3xl font-bold mb-4">{article.title}</h1>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <span>By {getAuthorName(article.author)}</span>
            {article.publish_date && (
              <>
                <span>•</span>
                <span>{format(new Date(article.publish_date), 'PPP')}</span>
              </>
            )}
          </div>
          
          {article.lead_text && (
            <div className="text-lg font-medium mb-6 italic text-gray-700">
              {article.lead_text}
            </div>
          )}
          
          <div 
            ref={contentRef}
            className="prose prose-lg max-w-none article-content" 
            dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
          />
          
          {article.tags && article.tags.length > 0 && (
            <div className="mt-8 pt-4 border-t">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag: any, i: number) => (
                  <span key={i} className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticlePreviewPage;
